/*
 * MICADOR CUSTOM — inline header search popover state.
 *
 * Restores the behaviour that was lost on 21 Jun 2026 when the Impact 7.1.0
 * update replaced assets/theme.js.
 *
 * Until then this lived as a Micador modification INSIDE the vendor
 * PredictiveSearch class in assets/theme.js: a _setOpen() method plus focus,
 * document-pointerdown and Escape handlers. Being inside a vendor file, it was
 * destroyed by the update exactly as sections/header.liquid was — and unlike
 * header.liquid, nobody noticed, because the symptom is a search bar that
 * renders perfectly and simply never shows results.
 *
 * It is reimplemented here as a standalone Micador-owned asset so a vendor
 * update cannot take it again. It touches no vendor class internals: it only
 * reads the input's value and sets attributes, so it does not care how
 * Maestrooo implement predictive search now or later.
 *
 * What it drives:
 *   [data-open] on the inline <predictive-search>
 *       snippets/header-inline-search.liquid hides [slot="results"] and
 *       [slot="loading"] unless this attribute is present.
 *   .search-popover-open on <store-header>
 *       snippets/header-v3-grid.liquid (item 9) lifts store-header to z-index 25
 *       so nav items in .header__nav-row (z-index 20) stop bleeding through.
 *
 * Scope: the inline instance only. The search drawer's own <predictive-search>
 * is deliberately untouched — it has its own open/close lifecycle.
 */
(function () {
  'use strict';

  var INLINE = '.header-inline-search';
  var wired = new WeakSet();

  function wire(root) {
    var container = (root || document).querySelector(INLINE);
    if (!container) return;

    var ps = container.querySelector('predictive-search');
    var form = container.querySelector('form');
    if (!ps || !form || wired.has(ps)) return;

    var input = form.querySelector('input[name="q"]');
    if (!input) return;

    var storeHeader = container.closest('store-header');
    wired.add(ps);

    function setOpen(isOpen) {
      if (isOpen) {
        ps.setAttribute('data-open', '');
      } else {
        ps.removeAttribute('data-open');
      }
      if (storeHeader) {
        storeHeader.classList.toggle('search-popover-open', isOpen);
      }
    }

    // Open whenever there is something to show, close the moment the field empties.
    // Not debounced: this is only the popover's visibility. The vendor component
    // still owns the debounced fetch of the results themselves.
    input.addEventListener('input', function () {
      setOpen(input.value !== '');
    });

    // Returning to a field that still has a query re-opens the results.
    input.addEventListener('focus', function () {
      if (input.value !== '') setOpen(true);
    });

    // Click anywhere outside the component or its form dismisses it.
    document.addEventListener('pointerdown', function (e) {
      if (!ps.contains(e.target) && !form.contains(e.target)) setOpen(false);
    });

    // Escape dismisses and returns focus to the input.
    document.addEventListener('keydown', function (e) {
      if (e.key === 'Escape' && ps.hasAttribute('data-open')) {
        setOpen(false);
        input.focus();
      }
    });

    form.addEventListener('reset', function () {
      setOpen(false);
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () { wire(document); });
  } else {
    wire(document);
  }

  // Theme editor re-renders the header section without a page load.
  document.addEventListener('shopify:section:load', function (e) {
    wire(e.target || document);
  });
})();
